<template>
	<!-- Floating toggle (right edge). Badge zeigt die Anzahl aktiver Alarme/Warnungen
	     des GEÖFFNETEN Graphen — ohne Graphen gibt es nichts zu zählen, also auch
	     keine Glocke. Sonst stand eine Zahl über einer Liste, die sie nicht enthielt. -->
	<button
		v-if="hasGraph"
		type="button"
		class="btn btn-circle btn-sm fixed right-4 bottom-4 z-40 shadow-lg"
		:class="count > 0 ? 'btn-error' : 'btn-ghost bg-base-200'"
		:title="t('alerts.widget.title')"
		@click="isOpen = !isOpen">
		<Bell class="h-4 w-4" />
		<span
			v-if="count > 0"
			class="badge badge-warning badge-xs absolute -top-1 -right-1">
			{{ count }}
		</span>
	</button>

	<!-- Backdrop — startet unter der Navbar (top-16); z über dem DynamicHeader (z-100),
	     damit das Panel nicht von der Titelleiste verdeckt wird. -->
	<div v-if="hasGraph && isOpen" class="fixed top-16 right-0 bottom-0 left-0 z-[110] bg-black/20" @click="isOpen = false"></div>

	<!-- Slide-out panel rechts; top-16 = Höhe der oberen Navbar (Avatar/Sprache bleiben frei),
	     z-[120] liegt über dem DynamicHeader (z-100), sonst überdeckt die Titelleiste die Sidebar. -->
	<aside
		v-if="hasGraph"
		class="bg-base-200 fixed top-16 right-0 bottom-0 z-[120] flex w-[32rem] max-w-[95vw] flex-col shadow-2xl transition-transform duration-200"
		:class="isOpen ? 'translate-x-0' : 'translate-x-full'">
		<header class="border-base-300 flex items-center justify-between border-b p-3">
			<span class="font-semibold">{{ t('alerts.widget.title') }}</span>
			<button type="button" class="btn btn-ghost btn-circle btn-xs" @click="isOpen = false">
				<X class="h-4 w-4" />
			</button>
		</header>

		<!-- Same two tabs as the Alert-Flags modal on the chart: the alarm rule's
		     verdicts, and what the stale-data cron flagged as missing. The tabs
		     sit outside the scroll area so the list's sticky filter still works. -->
		<div class="tabs tabs-box mx-2 mt-2">
			<div class="tab" :class="{ 'tab-active': tab === 'alarms' }" @click="tab = 'alarms'">
				{{ t('alerts.widget.tabAlarms') }}
			</div>
			<div class="tab" :class="{ 'tab-active': tab === 'flags' }" @click="tab = 'flags'">
				{{ t('alerts.widget.tabFlags') }}
			</div>
		</div>
		<div class="flex-1 overflow-auto p-2">
			<!-- v-show: the alarm list loads on mount, switching tabs must not reload it. -->
			<AlertStatusList v-show="tab === 'alarms'" />
			<AlertMissingDataList
				v-if="tab === 'flags'"
				:flags="activeGraph?.alertLogFlags || {}"
				:equations="activeGraph?.equations || []" />
		</div>
	</aside>
</template>

<script setup lang="ts">
	import { computed, ref } from 'vue'
	import { Bell, X } from 'lucide-vue-next'
	import { useI18n } from 'vue-i18n'
	import { useBaseStore } from '@/stores/base.store'
	import { useAlerts } from './useAlerts'
	import AlertStatusList from './AlertStatusList.vue'
	import AlertMissingDataList from './AlertMissingDataList.vue'
	import { _translations } from './_translations'

	const { t } = useI18n(_translations)
	const alertsApi = useAlerts()
	const baseStore = useBaseStore()
	const hasGraph = computed(() => !!baseStore.getActiveIds.graphId)

	const isOpen = ref(false)
	const tab = ref<'alarms' | 'flags'>('alarms')
	// The open graph's document from the alerts store — it carries the
	// `alertLogFlags` the stale-data cron writes.
	const activeGraph = computed(() => {
		const id = String(baseStore.getActiveIds.graphId || '')
		return alertsApi.customGraphs.value.find((g) => String(g._id) === id)
	})
	// Rote Glocke + Badge nur für aktuell auslösende (Alarm/Warnung); die Liste
	// selbst (inkl. init) rendert AlertStatusList.
	const count = computed(() => alertsApi.configuredAlerts.value.filter((i) => i.alert || i.warn).length)
</script>
